/**
 * tailorMeasurementHelp.js — schema-agnostic per-field help dictionary
 * + AI confidence model + recommendation rules + critical-missing checker.
 *
 * Used by MeasurementGuide.js. ALL data is keyed by standardized field IDs
 * so adding a new garment schema requires zero changes here.
 */

// ─── Per-field help cards (universal — keyed by field id) ──────────────────
// Lookup is done by exact id match; falls back to a generic card.
export const FIELD_HELP = {
  bust:             { tape: 'Around fullest part of bust', direction: 'Horizontal · parallel to floor',
                      mistakes: ['Tape too tight', 'Tape slipping down at the back', 'Customer holding breath'],
                      impact: 'Wrong bust = entire blouse silhouette off. Most critical female measurement.' },
  upper_bust:       { tape: 'Around chest, just under armpits, above bust',
                      direction: 'Horizontal · level',
                      mistakes: ['Including bust fullness', 'Tape sloping downward at back'],
                      impact: 'Determines true frame size. Used to choose pattern size when bust is enhanced.' },
  under_bust:       { tape: 'Snug around the ribcage, just below the bust',
                      direction: 'Horizontal · firm but not tight',
                      mistakes: ['Tape over bust', 'Loose tape'],
                      impact: 'Defines bra band line and dart placement.' },
  waist:            { tape: 'Around natural waist (narrowest point)',
                      direction: 'Horizontal · firm contact',
                      mistakes: ['Measuring at hip line', 'Sucking in stomach'],
                      impact: 'Wrong waist → puckers or pulls at darts.' },
  hip:              { tape: 'Around fullest part of hips/seat',
                      direction: 'Horizontal · level',
                      mistakes: ['Measuring high on the hip', 'Tape catching at back'],
                      impact: 'Affects skirt and bottom-wear flow.' },
  shoulder:         { tape: 'From shoulder bone tip → other shoulder bone tip across the back',
                      direction: 'Across the back · curved over the shoulders',
                      mistakes: ['Measuring across the front', 'Tape too loose'],
                      impact: 'Wrong shoulder = sleeve cap sitting off-shoulder or pinching armpit.' },
  chest:            { tape: 'Around fullest part of chest, under armpits',
                      direction: 'Horizontal · parallel to floor',
                      mistakes: ['Tape over shoulder blades', 'Customer flexing'],
                      impact: 'Primary fit driver for men\'s tops.' },
  neck:             { tape: 'Around base of neck where collar sits',
                      direction: 'Horizontal · 1 finger easing',
                      mistakes: ['Including Adam\'s apple', 'Too tight'],
                      impact: 'Collar choke or gap if wrong.' },
  sleeve_length:    { tape: 'Shoulder bone tip → desired sleeve hem',
                      direction: 'Down the outside of the arm · arm relaxed',
                      mistakes: ['Arm bent', 'Measuring from neck'],
                      impact: 'Wrong sleeve length looks immediately off.' },
  sleeve_round:     { tape: 'Around bicep at fullest part',
                      direction: 'Horizontal · arm relaxed',
                      mistakes: ['Arm flexed', 'Tape sliding'],
                      impact: 'Tight bicep restricts movement; loose looks unfinished.' },
  armhole:          { tape: 'Around the armscye opening',
                      direction: 'Curved · follow body contour',
                      mistakes: ['Measuring straight', 'Including underarm fullness'],
                      impact: 'Wrong armhole = pull lines or armpit puckers.' },
  bicep:            { tape: 'Around the upper arm at thickest point',
                      direction: 'Horizontal · arm relaxed',
                      mistakes: ['Arm flexed'],
                      impact: 'Sleeve grip and silhouette.' },
  cuff:             { tape: 'Around wrist, with watch space',
                      direction: 'Snug · 1 finger ease',
                      mistakes: ['Tight against bone', 'No watch space'],
                      impact: 'Sleeve hem comfort.' },
  blouse_length:    { tape: 'Shoulder seam → desired hem',
                      direction: 'Vertical · down the front · over the bust',
                      mistakes: ['Measuring side-only', 'Not accounting for hem turn-up'],
                      impact: 'Whole proportion of blouse depends on this.' },
  back_length:      { tape: 'Back neck base → hem',
                      direction: 'Vertical · straight down the spine',
                      mistakes: ['Tape going over shoulder blade'],
                      impact: 'Front and back balance.' },
  side_length:      { tape: 'Bottom of armhole → hem',
                      direction: 'Vertical · straight down the side seam',
                      mistakes: ['Measuring with arm raised'],
                      impact: 'Side seam alignment.' },
  princess_length:  { tape: 'Shoulder seam → over bust apex → hem',
                      direction: 'Down the princess seam line',
                      mistakes: ['Skipping over the bust'],
                      impact: 'Princess seam tailoring lines.' },
  apex_distance:    { tape: 'Tip of one bust → tip of other bust',
                      direction: 'Horizontal',
                      mistakes: ['Estimating instead of measuring', 'Wrong bra'],
                      impact: 'Dart placement & cup positioning.' },
  front_neck_depth: { tape: 'Shoulder line → desired front neckline depth',
                      direction: 'Vertical · centre of chest',
                      mistakes: ['Curving the tape'],
                      impact: 'Modesty + neckline style depend on this.' },
  back_neck_depth:  { tape: 'Shoulder line → desired back neckline depth',
                      direction: 'Vertical · centre of back',
                      mistakes: ['Sloping tape'],
                      impact: 'Hook placement and back style.' },
  neck_width:       { tape: 'Across the front neckline opening, shoulder seam to seam',
                      direction: 'Horizontal · across collar bone',
                      mistakes: ['Measuring at the throat'],
                      impact: 'Boat neck / round neck silhouette.' },
  darts:            { tape: 'Tailor decision based on bust shape',
                      direction: 'N/A — design',
                      mistakes: ['Skipping darts on full bust', 'Over-darting on small bust'],
                      impact: 'Bust shaping & smooth front.' },
  cup_shaping:      { tape: 'Standard 1 cm padding allowance',
                      direction: 'N/A — design',
                      mistakes: ['Forgetting on padded blouses'],
                      impact: 'Cup smoothness and silhouette.' },
  margin_allowance: { tape: 'Tailor reserve at side seams',
                      direction: 'N/A — construction',
                      mistakes: ['Skipping margin', 'Trimming during cutting'],
                      impact: 'Future alteration capability.' },
  inseam:           { tape: 'Crotch seam → ankle, inside leg',
                      direction: 'Vertical · inner leg',
                      mistakes: ['Standing pose changing length'],
                      impact: 'Trouser hem position.' },
  outseam:          { tape: 'Top of waist → ankle, outside leg',
                      direction: 'Vertical · outer leg',
                      mistakes: ['Tape catching at hip'],
                      impact: 'Total trouser silhouette.' },
  rise:             { tape: 'Crotch → top of waistband',
                      direction: 'Vertical · seated position',
                      mistakes: ['Standing measurement'],
                      impact: 'Crotch comfort + waist sit.' },
  thigh:            { tape: 'Around fullest part of thigh',
                      direction: 'Horizontal · standing relaxed',
                      mistakes: ['Standing too wide'],
                      impact: 'Thigh fit / mobility.' },
  knee:             { tape: 'Around the knee',
                      direction: 'Horizontal · standing',
                      mistakes: ['Bent knee'],
                      impact: 'Knee fit / drape.' },
  bottom_hem:       { tape: 'Trouser hem opening width (one side)',
                      direction: 'Horizontal',
                      mistakes: ['Including doubled fabric'],
                      impact: 'Final silhouette taper.' },
  shirt_length:     { tape: 'Back of neck → shirt hem',
                      direction: 'Vertical · down spine',
                      mistakes: ['Front-only measurement'],
                      impact: 'Tucking + sit on hip.' },
  kurta_length:     { tape: 'Shoulder → kurta hem',
                      direction: 'Vertical · down the front',
                      mistakes: ['Curving over knees'],
                      impact: 'Whole proportion.' },
  pajama_length:    { tape: 'Waist → ankle',
                      direction: 'Vertical · outer leg',
                      mistakes: ['Standing pose mismatch'],
                      impact: 'Pajama hem position.' },
  pajama_bottom:    { tape: 'Pajama hem opening width',
                      direction: 'Horizontal',
                      mistakes: ['Doubled fabric'],
                      impact: 'Hem taper.' },
  collar_height:    { tape: 'Stand-collar height when stitched',
                      direction: 'Vertical',
                      mistakes: ['Too tall is uncomfortable'],
                      impact: 'Neck profile.' },
  placket_length:   { tape: 'Front opening total length',
                      direction: 'Vertical',
                      mistakes: ['Skipping turnover'],
                      impact: 'Button placement.' },
  side_slit:        { tape: 'Slit length from hem upward',
                      direction: 'Vertical',
                      mistakes: ['Slits uneven'],
                      impact: 'Movement + drape.' },
};

// Generic fallback help card for any field not listed above.
export const GENERIC_HELP = {
  tape: 'Use a flexible tape measure. Take measurement over fitted clothing.',
  direction: 'Per standard tailoring practice.',
  mistakes: ['Tape twisted', 'Tape too loose or too tight', 'Customer not in natural posture'],
  impact: 'Affects fit accuracy and final garment silhouette.',
};

// ─── Per-field beginner-mode sewing notes ─────────────────────────────────
export const BEGINNER_NOTES = {
  bust:             'Cut bust panel +1 cm wider for ease, especially in cotton.',
  shoulder:         'Reinforce shoulder seam with thin interlining.',
  armhole:          'Add 0.5 cm ease in armhole for movement.',
  blouse_length:    'Add 4 cm hem allowance.',
  sleeve_length:    'Add 3 cm cuff allowance.',
  front_neck_depth: 'Bind raw edge with bias tape for clean finish.',
  apex_distance:    'Mark dart tips 2 cm short of apex point.',
  margin_allowance: 'Always cut side seams with 2 cm margin for letting out later.',
  inseam:           'Add 4 cm hem allowance for trouser cuff.',
  rise:             'Reinforce crotch seam — common stress point.',
  collar_height:    'Use medium-weight fusing for crisp stand.',
};

// ─── AI confidence model — derives confidence per measurement ─────────────
// Strategy: source-based base, adjusted by field type.
const SOURCE_BASE = {
  scan_ease: 92,
  scan: 92,
  choice: 88,
  edited: 85,
  rule: 80,
  recorded: 75,
  customer: 92,
  derived:  68,
  default:  55,
  pending:  0,
};
// Field-specific confidence adjustment (some fields are inherently noisier)
const FIELD_ADJ = {
  bust: +4, chest: +4, shoulder: -2, waist: 0, hip: 0,
  apex_distance: -8,           // hard to derive precisely
  front_neck_depth: -4, back_neck_depth: -4,
  cup_shaping: -10, margin_allowance: -10,
  darts: -15,
  inseam: +2, outseam: 0,
  cuff: -2,
};
export const computeConfidence = (field) => {
  if (field.value == null) return 0;
  const base = SOURCE_BASE[field.source] ?? 50;
  const adj = FIELD_ADJ[field.id] ?? 0;
  return Math.max(0, Math.min(99, base + adj));
};

// ─── Critical fields per schema (used for "missing critical" warnings) ────
const CRITICAL_PER_SCHEMA = {
  blouse:        ['bust', 'shoulder', 'blouse_length', 'sleeve_length', 'apex_distance', 'sleeve_round'],
  suit:          ['bust', 'waist', 'hip', 'shoulder', 'kurti_length', 'salwar_length'],
  kurta_pajama:  ['chest', 'shoulder', 'kurta_length', 'sleeve_length', 'pajama_length'],
  men_shirt:     ['chest', 'shoulder', 'sleeve_length', 'shirt_length', 'neck'],
  trouser:       ['waist', 'hip', 'inseam', 'bottom_hem'],
  sherwani:      ['chest', 'shoulder', 'length', 'sleeve_length', 'neck'],
};

/** Returns ordered list of critical fields that are missing/empty. */
export const findMissingCritical = (schemaKey, fieldsView) => {
  const ids = CRITICAL_PER_SCHEMA[schemaKey] || [];
  const byId = Object.fromEntries(fieldsView.map(f => [f.id, f]));
  return ids
    .map(id => byId[id])
    .filter(f => !f || f.value == null || f.value === '' || f.source === 'pending');
};

// ─── Tailor recommendation engine ─────────────────────────────────────────
// Derives smart suggestions from order context. Pure function — no side
// effects. Returns ordered list of {tone, title, body}. Each rule is small
// and additive so future garments need only register new rules here.
const _toLower = (v) => {
  if (v == null) return '';
  if (typeof v === 'string') return v.toLowerCase();
  if (Array.isArray(v)) return v.join(' ').toLowerCase();
  return String(v).toLowerCase();
};
export const buildRecommendations = ({
  schemaKey, customizations = {}, measurements = {}, fitPref,
}) => {
  const out = [];
  const c = customizations || {};
  const m = measurements || {};
  const fp = _toLower(fitPref || m.fit);
  const work = _toLower(c.work);
  const fabric = _toLower(c.fabric);
  const back = _toLower(c.back);
  const front = _toLower(c.front || c.neckline);
  const sleeves = _toLower(c.sleeves);

  // Fit-driven ease
  if (fp === 'loose' || fp === 'comfort' || fp === 'relaxed') {
    out.push({ tone: 'info',
      title: 'Comfort fit selected',
      body: 'Add +1.5 cm ease at bust/chest and +1 cm at armhole for relaxed drape.' });
  } else if (fp === 'slim' || fp === 'tighten') {
    out.push({ tone: 'info',
      title: 'Slim fit selected',
      body: 'Reduce bust ease to +0.5 cm. Use 8 darts (4 bust, 4 waist) for body-hug silhouette.' });
  }

  // Fabric / work intensity
  if (work.includes('heavy') || work.includes('zardo') || work.includes('embroidery')) {
    out.push({ tone: 'warn',
      title: 'Heavy embroidery detected',
      body: 'Increase margin allowance to +3 cm. Add muslin lining to support weight and prevent fabric stress.' });
  }
  if (fabric.includes('silk') || fabric.includes('satin')) {
    out.push({ tone: 'info',
      title: 'Silk/satin fabric',
      body: 'Use sharp pins inside seam allowance only. Cut single-layer to avoid slippage.' });
  }
  if (fabric.includes('net') || fabric.includes('chiffon')) {
    out.push({ tone: 'warn',
      title: 'Sheer fabric',
      body: 'Recommend full inner lining. Use French seams for clean finish.' });
  }

  // Style cuts
  if (back.includes('deep') || back.includes('v back') || back.includes('low')) {
    out.push({ tone: 'warn',
      title: 'Deep back neck selected',
      body: 'Add reinforced lining + 2 hooks at hem of neckline opening to prevent gape.' });
  }
  if (front.includes('sweetheart') || front.includes('deep')) {
    out.push({ tone: 'info',
      title: 'Deep / sweetheart neckline',
      body: 'Use bust cup pads for shape support. Bind neckline with bias tape.' });
  }
  if (sleeves.includes('bell') || sleeves.includes('flare')) {
    out.push({ tone: 'info',
      title: 'Bell / flared sleeves',
      body: 'Cut sleeve at 1.5x bicep round at hem. Hem with rolled-edge for fluid movement.' });
  }

  // Schema-specific defaults
  if (schemaKey === 'blouse' && !m.apex_distance) {
    out.push({ tone: 'tip',
      title: 'Apex distance not captured',
      body: 'Use 20% of bust as a starting estimate. Confirm with customer at first fitting.' });
  }
  if ((schemaKey === 'sherwani' || schemaKey === 'kurta_pajama') && !m.bicep) {
    out.push({ tone: 'tip',
      title: 'Bicep not captured',
      body: 'Estimated from chest. Confirm with customer for slim sleeves.' });
  }

  // No specific rule fired → at least give a clean default tip
  if (out.length === 0) {
    out.push({ tone: 'tip',
      title: 'Standard production',
      body: 'Use default 2 cm margin allowance and standard dart placement.' });
  }
  return out;
};
